/**
 * ========================================
 * Dashboard Page - 관리자 대시보드
 * ========================================
 *
 * 통계 및 주요 지표 표시
 * - 총 사용자 수
 * - 총 활성 스팟 수
 * - 오늘의 알림 전송 수
 * - 이번 달 방문 수
 * - 인기 스팟 Top 5
 */

import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { 
  HiUsers, 
  HiLocationMarker, 
  HiBell, 
  HiChartBar,
  HiLogout,
  HiSparkles,
  HiTrendingUp,
  HiTrendingDown,
  HiClock,
  HiViewGrid,
  HiMap,
  HiCog,
  HiLockClosed
} from 'react-icons/hi';
import { format } from 'date-fns';
import { analyticsAPI, adminAuth } from '../services/api.service';
import { formatMeasurement, getGradeBadgeClass, getGradeDisplay } from '../utils/measurementUtils';
import ChangePasswordModal from '../components/ChangePasswordModal';

export default function Dashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('measurement'); // 'popular' or 'measurement'
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  // 대시보드 데이터 조회
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dashboard'],
    queryFn: analyticsAPI.getDashboard,
    retry: 1,
  });

  // 에러 시 로그인 페이지로 리다이렉트
  useEffect(() => {
    if (error?.response?.status === 401) {
      navigate('/login');
    }
  }, [error, navigate]);

  // 탭 전환 시 데이터 새로 조회
  useEffect(() => {
    if (data) {
      refetch();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // 데이터 추출 (hooks는 항상 같은 순서로 호출되어야 함)
  const stats = data?.data?.overview || {};
  const topSpots = data?.data?.top_spots || [];

  // 측정수치 Top5 (최고 측정 수치 기준 정렬)
  // useMemo는 조건부 렌더링 이전에 호출되어야 함
  const topMeasurementSpots = useMemo(() => {
    if (!topSpots || topSpots.length === 0) return [];
    return [...topSpots]
      .filter(spot => spot.max_measurement && parseFloat(spot.max_measurement) > 0)
      .sort((a, b) => parseFloat(b.max_measurement) - parseFloat(a.max_measurement))
      .slice(0, 5);
  }, [topSpots]);

  const handleLogout = () => {
    adminAuth.logout();
    navigate('/login');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-theme-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-primary mx-auto mb-4"></div>
          <p className="text-theme-text-secondary">데이터 로딩 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-theme-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">데이터를 불러오는데 실패했습니다.</p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-theme-primary text-white rounded-lg hover:bg-indigo-500"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  const adminInfo = adminAuth.getCurrentAdmin();

  return (
    <div className="min-h-screen bg-theme-background">
      {/* Header */}
      <motion.header
        className="bg-gradient-to-r from-indigo-600 via-pink-600 to-indigo-600 backdrop-blur-xl shadow-2xl border-b border-white/20"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-[95%] mx-auto px-2 sm:px-4 lg:px-6 py-5">
          <div className="flex justify-between items-center">
            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="flex items-center gap-4">
                <motion.div
                  className="p-1 bg-white/20 backdrop-blur-md rounded-full shadow-lg border-2 border-white/40 relative overflow-hidden"
                  whileHover={{ scale: 1.1 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <img 
                    src="/Frame 34.png" 
                    alt="시스템 아이콘" 
                    className="w-10 h-10 object-cover rounded-full"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const fallback = e.target.parentElement.querySelector('.icon-fallback');
                      if (fallback) fallback.style.display = 'block';
                    }}
                  />
                  <HiSparkles className="w-10 h-10 text-white icon-fallback hidden rounded-full" />
                </motion.div>
                <div>
                  <h1 className="text-2xl font-extrabold text-white tracking-tight drop-shadow-lg">
                    팔팔구구 <span className="bg-gradient-to-r from-yellow-200 to-pink-200 bg-clip-text text-transparent">메타보맵</span>
                  </h1>
                  <p className="text-sm font-medium text-white/90 mt-0.5">관리자 대시보드</p>
                </div>
              </div>
            </motion.div>
            <motion.div
              className="flex items-center space-x-4"
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="text-right">
                <p className="text-sm font-bold text-white drop-shadow-md">{adminInfo?.username || 'Admin'}</p>
                <p className="text-xs font-medium text-white/80">{adminInfo?.role || 'admin'}</p>
              </div>
              <motion.button
                onClick={() => setIsPasswordModalOpen(true)}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-white/20 backdrop-blur-md border border-white/30 rounded-xl hover:bg-white/30 flex items-center gap-2 shadow-lg transition-all"
                whileHover={{ scale: 1.05, y: -1 }}
                whileTap={{ scale: 0.95 }}
              >
                <HiLockClosed className="w-4 h-4" />
                비밀번호 변경
              </motion.button>
              <motion.button
                onClick={handleLogout}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-white/20 backdrop-blur-md border border-white/30 rounded-xl hover:bg-white/30 flex items-center gap-2 shadow-lg transition-all"
                whileHover={{ scale: 1.05, y: -1 }}
                whileTap={{ scale: 0.95 }}
              >
                <HiLogout className="w-4 h-4" />
                로그아웃
              </motion.button>
            </motion.div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="max-w-[95%] mx-auto px-2 sm:px-4 lg:px-6 py-8">
        {/* Navigation Tabs */}
        <motion.div
          className="mb-8 bg-theme-surface backdrop-blur-lg rounded-2xl shadow-lg border-2 border-theme-primary/20 p-2"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <nav className="flex space-x-2">
            <motion.button
              onClick={() => navigate('/dashboard')}
              className="py-3 px-6 rounded-xl font-semibold text-sm flex items-center gap-2 relative bg-gradient-to-r from-theme-primary to-theme-secondary text-white shadow-lg"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <HiViewGrid className="w-5 h-5" />
              대시보드
            </motion.button>
            <motion.button
              onClick={() => navigate('/spots')}
              className="py-3 px-6 rounded-xl font-medium text-sm flex items-center gap-2 text-theme-text-secondary hover:text-theme-primary hover:bg-theme-surface/50 transition-all"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <HiMap className="w-5 h-5" />
              스팟 관리
            </motion.button>
            <motion.button
              onClick={() => navigate('/spots?tab=sensorData')}
              className="py-3 px-6 rounded-xl font-medium text-sm flex items-center gap-2 text-theme-text-secondary hover:text-theme-primary hover:bg-theme-surface/50 transition-all"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <HiChartBar className="w-5 h-5" />
              스팟별 측정자료 조회
            </motion.button>
            <motion.button
              onClick={() => navigate('/grade-settings')}
              className="py-3 px-6 rounded-xl font-medium text-sm flex items-center gap-2 text-theme-text-secondary hover:text-theme-primary hover:bg-theme-surface/50 transition-all"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <HiCog className="w-5 h-5" />
              등급 판정 기준
            </motion.button>
          </nav>
        </motion.div>

        {/* Stats Grid - 주석처리 */}
        {/* <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          {/* Total Users */}
          {/* <motion.div
            className="bg-theme-surface backdrop-blur-lg rounded-2xl shadow-xl p-6 border-2 border-theme-primary/30"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            whileHover={{ y: -8, scale: 1.02, shadow: "2xl" }}
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="p-3.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg">
                  <HiUsers className="w-7 h-7 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-semibold text-theme-text-secondary">총 사용자</p>
                <p className="text-3xl font-extrabold text-theme-primary">{stats.total_users || 0}</p>
              </div>
            </div>
          </motion.div> */}

          {/* Total Spots */}
          {/* <motion.div
            className="bg-theme-surface backdrop-blur-lg rounded-2xl shadow-xl p-6 border-2 border-theme-primary/30"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            whileHover={{ y: -8, scale: 1.02, shadow: "2xl" }}
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="p-3.5 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl shadow-lg">
                  <HiLocationMarker className="w-7 h-7 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-semibold text-theme-text-secondary">활성 스팟</p>
                <p className="text-3xl font-extrabold text-theme-primary">{stats.total_active_spots || 0}</p>
              </div>
            </div>
          </motion.div> */}

          {/* Today's Notifications */}
          {/* <motion.div
            className="bg-theme-surface backdrop-blur-lg rounded-2xl shadow-xl p-6 border-2 border-theme-primary/30"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            whileHover={{ y: -8, scale: 1.02, shadow: "2xl" }}
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="p-3.5 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-2xl shadow-lg">
                  <HiBell className="w-7 h-7 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-semibold text-theme-text-secondary">오늘 알림</p>
                <p className="text-3xl font-extrabold text-theme-primary">{stats.total_notifications_today || 0}</p>
              </div>
            </div>
          </motion.div> */}

          {/* Monthly Visits */}
          {/* <motion.div
            className="bg-theme-surface backdrop-blur-lg rounded-2xl shadow-xl p-6 border-2 border-theme-primary/30"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            whileHover={{ y: -8, scale: 1.02, shadow: "2xl" }}
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="p-3.5 bg-gradient-to-br from-theme-secondary to-pink-600 rounded-2xl shadow-lg">
                  <HiChartBar className="w-7 h-7 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-semibold text-theme-text-secondary">이번 달 방문</p>
                <p className="text-3xl font-extrabold text-theme-primary">{stats.total_visits_this_month || 0}</p>
              </div>
            </div>
          </motion.div>
        </div> */}

        {/* Tab Navigation */}
        <motion.div
          className="mb-6 bg-theme-surface backdrop-blur-lg rounded-2xl shadow-lg border-2 border-theme-primary/20 p-2"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <nav className="flex space-x-2">
            <motion.button
              onClick={() => setActiveTab('measurement')}
              className={`py-3 px-6 rounded-xl font-medium text-sm flex items-center gap-2 transition-all ${
                activeTab === 'measurement'
                  ? 'bg-gradient-to-r from-theme-primary to-theme-secondary text-white shadow-lg'
                  : 'text-theme-text-secondary hover:text-theme-primary hover:bg-theme-surface/50'
              }`}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <HiChartBar className="w-5 h-5" />
              측정수치 Top 5
            </motion.button>
            <motion.button
              onClick={() => setActiveTab('popular')}
              className={`py-3 px-6 rounded-xl font-medium text-sm flex items-center gap-2 transition-all ${
                activeTab === 'popular'
                  ? 'bg-gradient-to-r from-theme-primary to-theme-secondary text-white shadow-lg'
                  : 'text-theme-text-secondary hover:text-theme-primary hover:bg-theme-surface/50'
              }`}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <HiLocationMarker className="w-5 h-5" />
              인기 스팟 Top 5
            </motion.button>
          </nav>
        </motion.div>

        {/* Top Spots Table */}
        <motion.div
          className="bg-theme-surface backdrop-blur-lg rounded-2xl shadow-xl border-2 border-theme-primary/20 overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <div className="px-6 py-5 bg-theme-surface border-b border-theme-primary/20">
            <h3 className="text-xl font-extrabold text-theme-text">
              {activeTab === 'measurement' ? '측정수치 Top 5' : '인기 스팟 Top 5'}
            </h3>
            <p className="mt-1 text-sm font-medium text-theme-text-secondary">
              {activeTab === 'measurement' ? '최고 측정 수치 기준' : '최근 30일 기준'}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-theme-primary/20">
              <thead className="bg-theme-surface">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-theme-text-secondary uppercase tracking-wider">
                    순위
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-theme-text-secondary uppercase tracking-wider">
                    스팟명
                  </th>
                  {activeTab === 'popular' && (
                    <th className="px-6 py-4 text-left text-xs font-bold text-theme-text-secondary uppercase tracking-wider">
                      방문 통계
                    </th>
                  )}
                  <th className="px-6 py-4 text-left text-xs font-bold text-theme-text-secondary uppercase tracking-wider">
                    평균 측정 수치 (µT)
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-theme-text-secondary uppercase tracking-wider">
                    최고 측정 수치 (µT)
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-theme-text-secondary uppercase tracking-wider">
                    최저 측정 수치 (µT)
                  </th>
                </tr>
              </thead>
              <tbody className="bg-theme-background divide-y divide-theme-primary/20">
                {(() => {
                  const displaySpots = activeTab === 'measurement' ? topMeasurementSpots : topSpots;
                  return displaySpots.length > 0 ? (
                    displaySpots.map((spot, index) => (
                    <motion.tr 
                      key={index} 
                      className={`hover:bg-theme-surface/50 transition-all ${
                        index % 2 === 0 ? 'bg-theme-background' : 'bg-theme-surface/30'
                      }`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.6 + index * 0.1 }}
                    >
                      <td className="px-6 py-5 whitespace-nowrap">
                        <motion.div
                          className="flex items-center"
                          whileHover={{ scale: 1.1 }}
                        >
                          <span className={`inline-flex items-center justify-center w-10 h-10 rounded-2xl font-extrabold text-base shadow-lg ${
                            index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-white' :
                            index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-white' :
                            index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white' :
                            'bg-gradient-to-br from-theme-primary to-indigo-600 text-white'
                          }`}>
                            {index + 1}
                          </span>
                        </motion.div>
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap">
                        <div className="text-sm font-bold text-theme-text">{spot.spot_name}</div>
                      </td>
                      {activeTab === 'popular' && (
                        <td className="px-6 py-5 whitespace-nowrap">
                          <div className="space-y-1">
                            <div className="text-sm font-semibold text-theme-text">
                              총 <span className="text-theme-primary">{spot.visit_count || 0}</span>회
                            </div>
                            <div className="text-xs font-medium text-theme-text-secondary">
                              고유 <span className="text-theme-secondary">{spot.unique_visitors || 0}</span>명
                            </div>
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-5 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <HiChartBar className="w-4 h-4 text-theme-primary" />
                          <div>
                            <div className="text-sm font-bold text-theme-text">
                              {formatMeasurement(spot.avg_measurement || 0)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-2">
                          <HiTrendingUp className="w-4 h-4 text-green-400" />
                          <div>
                            <div className="text-sm font-bold text-green-400">
                              {formatMeasurement(spot.max_measurement || 0)}
                            </div>
                            {spot.max_grade_display && (
                              <div className="mt-1">
                                <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getGradeBadgeClass(spot.max_grade || '')}`}>
                                  {spot.max_grade_display}
                                </span>
                              </div>
                            )}
                            {spot.max_measurement_timestamp && (
                              <div className="text-xs font-medium text-theme-text-secondary flex items-center gap-1 mt-0.5">
                                <HiClock className="w-3 h-3" />
                                {format(new Date(spot.max_measurement_timestamp), 'yyyy-MM-dd HH:mm:ss')}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-2">
                          <HiTrendingDown className="w-4 h-4 text-red-400" />
                          <div>
                            <div className="text-sm font-bold text-red-400">
                              {formatMeasurement(spot.min_measurement || 0)}
                            </div>
                            {spot.min_measurement_timestamp && (
                              <div className="text-xs font-medium text-theme-text-secondary flex items-center gap-1 mt-0.5">
                                <HiClock className="w-3 h-3" />
                                {format(new Date(spot.min_measurement_timestamp), 'yyyy-MM-dd HH:mm:ss')}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </motion.tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={activeTab === 'popular' ? 6 : 5} className="px-6 py-12 text-center">
                        <div className="text-theme-text-secondary text-sm font-medium">
                          {activeTab === 'measurement' ? '측정 데이터가 없습니다' : '방문 기록이 없습니다'}
                        </div>
                      </td>
                    </tr>
                  );
                })()}
              </tbody>
            </table>
          </div>
        </motion.div>
      </main>

      {/* 비밀번호 변경 모달 */}
      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
      />
    </div>
  );
}
